// /horizon — opportunity discovery surface.
//
// Per the locked plan in /Users/amir/.claude/plans/analyze-md-files-plans-ethereal-galaxy.md:
//
//   * Renders <EntityScreen entity="OPPORTUNITY"> from @eidollon/ui — the
//     canonical pane primitive (CLAUDE.md Iron Rule 11).
//   * IntentBar replaces the dead "DEADLINE D · ROWS · HITS" strip.
//   * Filter cockpit: rail (FacetPanel) + chip strip + advanced builder
//     (rail today; QueryBuilder lands in Phase C).
//   * Result surface is a 28px virtualized table (@eidollon/ui VirtualTable).
//   * AI Rail wired to /v1/ai/suggest (placeholder until services/inference
//     is online — Phase B). Defaults to empty/cold-start.
//   * URL is canonical: zod-validated TanStack Router search params encode
//     the FilterState, sort, density, and column selection.
//   * Search backend: POST /v1/search/opportunities (FTS today, hybrid
//     OpenSearch tomorrow). Aggregations: POST /v1/search/opportunities/aggregations.

import { ApiError, apiFetch, isInferenceUnavailableError } from '@/lib/api';
import { compactMoney, dateOnly } from '@/lib/format';
import { firstClassification, flagEmoji, jurisdictionName } from '@/lib/jurisdiction';
import { localizedField, useLocale } from '@/lib/locale';
import { useFilterRailLabels } from '@/lib/useFilterRailLabels';
import { EmptyState, Pill } from '@/primitives';
import { useAuth } from '@/state/auth';
import { useInferenceStatus } from '@/state/inference-status';
import type { api as a, filters as f, search as s } from '@eidollon/contracts';
import { sortPinnedFirst, usePinned } from '@eidollon/intel-feed-client';
import { type ColumnDef, EntityScreen, Pagination, PinStar, type SortState } from '@eidollon/ui';
import { SavedView, type SavedViewFetcher, decode, encode } from '@eidollon/ui-filters';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

/* ──────────────────────────────────────────────────────────────────────────
 *  URL state (zod-validated TanStack Router search params)
 * ────────────────────────────────────────────────────────────────────────── */

const horizonSearch = z.object({
  q: z.string().optional(),
  f: z.string().optional(), // base64url(JSON(FilterState))
  sort: z.string().optional(), // 'closing_date:asc'
  cols: z.string().optional(), // 'id,jx,agy,title,...'
  cursor: z.string().optional(),
  // 1-indexed page + page size for URL-persisted pagination. The back-button
  // lands the operator on the same page of the same sort/filter combo.
  page: z.coerce.number().int().min(1).max(20_000).optional(),
  pageSize: z.coerce.number().int().min(1).max(200).optional(),
});
type HorizonSearch = z.infer<typeof horizonSearch>;

export const Route = createFileRoute('/_authed/horizon')({
  validateSearch: horizonSearch,
  component: HorizonPage,
});

// Adapter from SavedView's fetcher contract → the terminal's auth-aware
// apiFetch. Module-scoped so the reference is stable across renders.
const savedViewFetcher: SavedViewFetcher = async ({ url, method, body }) =>
  apiFetch<unknown>(url, body !== undefined ? { method, body } : { method });

/* ──────────────────────────────────────────────────────────────────────────
 *  Filter schema for OPPORTUNITY entity
 *
 *  This is the source of truth for which fields are filter-able. Adding a
 *  field here is the only change needed to surface a new facet in the rail.
 * ────────────────────────────────────────────────────────────────────────── */

const opportunityFilterSchema: f.FilterSchema = {
  entity: 'OPPORTUNITY',
  // Default sort: newest-posted first. Operators want to see what just
  // dropped into the feed, not what's closing furthest in the future. They
  // can still flip to closing_date by clicking the CLOSE column.
  defaultSort: { field: 'publication_date', dir: 'desc' },
  fields: [
    {
      id: 'status',
      label: 'STATUS',
      type: 'multi-enum',
      pinned: [
        { value: 'active', label: 'active' },
        { value: 'expired', label: 'expired' },
        { value: 'awarded', label: 'awarded' },
        { value: 'cancelled', label: 'cancelled' },
      ],
    },
    {
      id: 'jurisdiction',
      label: 'JURISDICTION',
      type: 'multi-enum',
      pinned: [
        { value: 'US', label: '🇺🇸 US' },
        { value: 'GB', label: '🇬🇧 UK' },
        { value: 'FR', label: '🇫🇷 France' },
        { value: 'DE', label: '🇩🇪 Germany' },
        { value: 'CA', label: '🇨🇦 Canada' },
        { value: 'EU', label: '🇪🇺 EU' },
      ],
    },
    {
      id: 'estimated_value',
      label: 'EST VALUE',
      type: 'money-range',
    },
    {
      id: 'procurement_category',
      label: 'CATEGORY',
      type: 'multi-enum',
    },
    {
      id: 'procurement_method',
      label: 'METHOD',
      type: 'multi-enum',
    },
    {
      id: 'notice_type',
      label: 'NOTICE',
      type: 'multi-enum',
    },
    {
      id: 'closing_date',
      label: 'CLOSING',
      type: 'date-range',
    },
    {
      id: 'publication_date',
      label: 'POSTED',
      type: 'date-range',
    },
  ],
};

/* ──────────────────────────────────────────────────────────────────────────
 *  Page
 * ────────────────────────────────────────────────────────────────────────── */

function HorizonPage() {
  const navigate = useNavigate({ from: '/horizon' });
  const { locale } = useLocale();
  const filterRailLabels = useFilterRailLabels();
  const search = Route.useSearch() as HorizonSearch;

  // Decode the URL `f` param into a FilterState. If the URL is invalid we
  // recover gracefully (clear filters, surface an error toast in the future).
  const state: f.FilterState = useMemo(() => {
    if (!search.f) return {};
    try {
      return decode(search.f);
    } catch {
      return {};
    }
  }, [search.f]);

  const sort: SortState = useMemo(() => {
    // Default to newest-posted first (matches schema.defaultSort).
    const raw = search.sort ?? 'publication_date:desc';
    const [field, dir] = raw.split(':');
    return { field: field || 'publication_date', dir: dir === 'asc' ? 'asc' : 'desc' };
  }, [search.sort]);

  // Page is 1-indexed in the URL; default 1, default size 50, max 200.
  const page = search.page ?? 1;
  const pageSize = Math.min(200, Math.max(1, search.pageSize ?? 50));

  // Any change to filters / sort / q invalidates the current page (page 7 of
  // the OLD result set is meaningless on the NEW set). Reset to page 1.
  const setState = (next: f.FilterState) => {
    const enc = encode(next);
    const fParam = enc.kind === 'inline' ? enc.value || undefined : undefined;
    navigate({
      search: (prev) => ({ ...prev, f: fParam, cursor: undefined, page: undefined }),
    });
  };
  const setSort = (next: SortState) => {
    navigate({
      search: (prev) => ({
        ...prev,
        sort: `${next.field}:${next.dir}`,
        cursor: undefined,
        page: undefined,
      }),
    });
  };
  const setQ = (next: string) => {
    const trimmed = next.trim();
    navigate({
      search: (prev) => ({
        ...prev,
        q: trimmed || undefined,
        cursor: undefined,
        page: undefined,
      }),
    });
  };
  const setPage = (next: number) => {
    navigate({
      search: (prev) => ({ ...prev, page: next > 1 ? next : undefined, cursor: undefined }),
    });
  };
  const setPageSize = (next: number) => {
    // Changing page size collapses to page 1 to avoid landing past the new
    // tail (e.g. page 7 @ 50/page → page 7 @ 200/page would skip far past
    // the user's last viewed row).
    navigate({
      search: (prev) => ({
        ...prev,
        pageSize: next === 50 ? undefined : next,
        page: undefined,
        cursor: undefined,
      }),
    });
  };

  // NL composer state. The submitted text writes to `?q=` (the FTS path);
  // Phase C wires /v1/ai/nl-rewrite which compiles to FilterAst chips.
  const [nlEditing, setNlEditing] = useState(false);
  const [nlValue, setNlValue] = useState(search.q ?? '');
  // Keep nlValue in sync if the URL changes externally.
  useEffect(() => {
    setNlValue(search.q ?? '');
  }, [search.q]);

  // Phase D: NL leftover surfaced as an "ignored" chip in the IntentBar.
  // After /v1/ai/nl-rewrite returns, the user decides whether to promote
  // the unmatched text to FTS (?q=) or drop it. Replaces the previous
  // auto-promote behaviour, which silently shipped potentially-irrelevant
  // tokens to the search backend.
  const [nlLeftover, setNlLeftover] = useState<string | null>(null);

  // `/` activates the NL composer.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/i.test(target.tagName)) return;
      if (e.key === '/' && !nlEditing) {
        e.preventDefault();
        setNlEditing(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nlEditing]);

  /* ────────────────────────────────────────────────────────────────────────
   *  Search query
   * ──────────────────────────────────────────────────────────────────────── */

  const searchBody: s.SearchRequest = useMemo(
    () => ({
      ...(search.q ? { q: search.q } : {}),
      state,
      sort,
      limit: pageSize,
      offset: (page - 1) * pageSize,
      personalize: true,
      // Render-language pivot: list cells use `row.title` directly (no
      // useTranslated hook). When the user is browsing in a non-English
      // locale we ask the API to swap title via the precomputed
      // `translations` row. Iron Rule 12 — zero LLM in the hot path.
      ...(locale && locale !== 'en' ? { target_lang: locale } : {}),
    }),
    [search.q, state, sort, page, pageSize, locale],
  );

  const opps = useQuery({
    queryKey: ['search', 'opportunities', searchBody],
    queryFn: async () =>
      apiFetch<{ rows: a.OpportunityRow[]; meta: s.SearchResponseMeta }>(
        '/v1/search/opportunities',
        // apiFetch JSON.stringifies its body internally; pass the object
        // directly. Double-stringifying makes the server see a JSON string
        // and reject with `validation_failed: Expected object, received string`.
        { method: 'POST', body: searchBody },
      ),
    placeholderData: (prev) => prev,
  });

  const aggBody: s.AggregationsRequest = useMemo(
    () => ({
      ...(search.q ? { q: search.q } : {}),
      state,
      fields: [
        'status',
        'jurisdiction',
        'procurement_category',
        'procurement_method',
        'notice_type',
      ],
    }),
    [search.q, state],
  );

  const aggs = useQuery({
    queryKey: ['search', 'opportunities', 'aggs', aggBody],
    queryFn: async () =>
      apiFetch<s.AggregationsResponse>('/v1/search/opportunities/aggregations', {
        method: 'POST',
        body: aggBody,
      }),
    staleTime: 30_000,
  });

  // Map aggregations → FacetPanel `counts` shape.
  const facetCounts = useMemo(() => {
    const out: Record<string, { value: string; label?: string; count: number }[]> = {};
    for (const [field, agg] of Object.entries(aggs.data?.fields ?? {})) {
      out[field] = agg.buckets.map((b) => ({ value: b.value, count: b.count }));
    }
    return out;
  }, [aggs.data]);

  /* ────────────────────────────────────────────────────────────────────────
   *  AI Rail — wired to /v1/ai/suggest. Hot path: precomputed daily_top_opps
   *  (zero LLM call in the user request flow). Cold-start tenants get an
   *  empty rail with the cold-start empty-state copy.
   *
   *  When services/inference is unreachable, apiFetch translates the API's
   *  503 inference_unavailable into InferenceUnavailableError and flips the
   *  global offline flag. We disable the query entirely in that state so
   *  the rail hides cleanly (ai={null} below) and we don't keep retrying.
   * ──────────────────────────────────────────────────────────────────────── */

  const aiOffline = useInferenceStatus((st) => st.offline);

  // Tenant-scoped pinned opportunities. The hook is no-op when tenantId is
  // null (pre-auth) — `ready` flips to true and `pinnedIds` stays empty so
  // the result table renders normally during the auth probe.
  const tenantId = useAuth((st) => st.me?.tenant_id ?? null);
  const pin = usePinned({ tenantId, entityKey: 'opp' });

  const ai = useQuery({
    queryKey: ['ai', 'suggest', 'horizon'],
    queryFn: async () =>
      apiFetch<s.SuggestResponse>('/v1/ai/suggest', {
        method: 'POST',
        body: { context: 'horizon' } as s.SuggestRequest,
      }),
    staleTime: 60_000, // suggestions only change after the nightly worker
    retry: (failureCount, err) => !isInferenceUnavailableError(err) && failureCount < 1,
    enabled: !aiOffline,
  });

  /* ────────────────────────────────────────────────────────────────────────
   *  Columns (stable; column-picker land in Phase A.2)
   * ──────────────────────────────────────────────────────────────────────── */

  const columns: ColumnDef<a.OpportunityRow>[] = useMemo(
    () => [
      {
        id: 'pin',
        label: '',
        width: 24,
        sticky: 'left',
        align: 'center',
        // Star is hover-revealed for unpinned rows, always-visible for pinned.
        // The CSS rule lives in global.css (Wave 4-A) — zero per-row JS hover.
        render: (r) => (
          <PinStar
            pinned={pin.pinnedSet.has(r.id)}
            onToggle={() => pin.toggle(r.id)}
            label={pin.pinnedSet.has(r.id) ? 'unpin opportunity' : 'pin opportunity'}
          />
        ),
      },
      {
        id: 'id',
        label: 'ID',
        width: 96,
        render: (r) => (
          <span style={{ color: 'var(--cyan, #2eb8c7)', fontFamily: 'var(--mono)' }}>
            {r.tender_id ?? r.reference_number ?? r.id.slice(0, 8)}
          </span>
        ),
      },
      {
        id: 'jx',
        label: 'JX',
        width: 52,
        // Flag + ISO code. The flag is a fast pre-attentive cue for scanning a
        // multi-jurisdiction feed; the code stays for unambiguous filtering.
        render: (r) => {
          const flag = flagEmoji(r.jurisdiction);
          return (
            <span title={jurisdictionName(r.jurisdiction)} style={{ fontVariant: 'tabular-nums' }}>
              {flag ? `${flag} ` : ''}
              <span style={{ color: 'var(--fg-2)' }}>{r.jurisdiction}</span>
            </span>
          );
        },
      },
      {
        id: 'agy',
        label: 'AGENCY',
        width: 200,
        render: (r) =>
          localizedField(r as unknown as Record<string, unknown>, 'agency_raw_name', locale) ??
          r.agency_raw_name ??
          '—',
      },
      {
        id: 'title',
        label: 'TITLE',
        width: 'flex',
        render: (r) => {
          const t = localizedField(r as unknown as Record<string, unknown>, 'title', locale);
          return t ?? r.title ?? <span style={{ color: 'var(--fg-3)' }}>—</span>;
        },
        sortable: true,
        sortField: '_relevance',
      },
      {
        id: 'code',
        label: 'CODE',
        width: 92,
        // Classification code across CPV (EU/UK OCDS) / UNSPSC / NAICS — each a
        // JSONB array that may be string[] or {code, description}[]. The scheme
        // label disambiguates which taxonomy the code belongs to. Never let a
        // raw object reach React's children.
        render: (r) => {
          const cls = firstClassification(r);
          if (!cls) return r.procurement_category ?? '—';
          return (
            <span style={{ fontFamily: 'var(--mono)' }}>
              {cls.code}
              <span style={{ color: 'var(--fg-3)', fontSize: '0.85em' }}> {cls.scheme}</span>
            </span>
          );
        },
      },
      {
        id: 'value',
        label: 'EST VALUE',
        width: 100,
        align: 'right',
        // Native currency, no FX conversion. £/€/C$/$ etc. — see compactMoney.
        render: (r) => {
          const v = compactMoney(r.estimated_value, r.currency);
          return (
            <span style={{ fontVariant: 'tabular-nums', color: v === '—' ? 'var(--fg-3)' : undefined }}>
              {v}
            </span>
          );
        },
      },
      {
        id: 'method',
        label: 'METHOD',
        width: 110,
        render: (r) => r.procurement_method ?? '—',
      },
      {
        id: 'closing',
        label: 'CLOSE',
        width: 132,
        align: 'right',
        sortable: true,
        sortField: 'closing_date',
        // Plain date only. The days-till-close pill was stripped — operators
        // found it noisy/misleading on historical (already-closed) rows.
        render: (r) => (r.closing_date ? dateOnly(r.closing_date) : '—'),
      },
      {
        id: 'stat',
        label: 'STAT',
        width: 64,
        render: (r) => <StatusPill status={r.status} />,
      },
    ],
    // Pin column reads from `pin.pinnedSet` + `pin.toggle`; declare them so
    // the column closures update when pins change. Locale flips title/agency
    // through localizedField, so the columns rebuild on locale change too.
    [pin.pinnedSet, pin.toggle, locale],
  );

  /* ────────────────────────────────────────────────────────────────────────
   *  Render
   * ──────────────────────────────────────────────────────────────────────── */

  const rawRows = opps.data?.rows ?? [];
  // Pinned rows always render at the top, in pin-order (newest pin first).
  // The user's chosen sort still applies to the unpinned tail. Stable when
  // `pin.pinnedIds` is empty (sortPinnedFirst returns the original array
  // reference in that case).
  const rows = useMemo(
    () => sortPinnedFirst(rawRows, pin.pinnedIds, (r) => r.id),
    [rawRows, pin.pinnedIds],
  );

  return (
    <EntityScreen<a.OpportunityRow>
      {...filterRailLabels}
      schema={opportunityFilterSchema}
      state={state}
      onStateChange={setState}
      sort={sort}
      onSortChange={setSort}
      rows={rows}
      rowKey={(r) => r.id}
      columns={columns}
      onRowClick={(r) => navigate({ to: '/brief/$id', params: { id: r.id } })}
      count={rows.length}
      total={opps.data?.meta.total ?? null}
      loading={opps.isFetching}
      hasMore={Boolean(opps.data?.meta.cursor)}
      pagination={
        <Pagination
          page={page}
          pageSize={pageSize}
          total={opps.data?.meta.total ?? null}
          hasMore={Boolean(opps.data?.meta.cursor)}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      }
      headerRightSlot={
        <SavedView
          entity="OPPORTUNITY"
          filters={state}
          onLoad={setState}
          fetcher={savedViewFetcher}
        />
      }
      ai={
        aiOffline
          ? null
          : {
              suggestions: ai.data?.suggestions ?? [],
              // W4-C: cold-start moved to /brief/$id (per-contract panel).
              // Horizon's AI rail no longer carries the cold-start placeholder;
              // when there are no suggestions it falls through to the "no
              // suggestions" empty state instead.
              coldStart: false,
              onOpen: (suggestion) => {
                // Fire-and-forget click signal so the nightly worker reinforces
                // this kind of opportunity in the tenant's interest vector.
                void apiFetch('/v1/search/feedback', {
                  method: 'POST',
                  body: {
                    query_id: crypto.randomUUID(),
                    opportunity_id: suggestion.entity_id,
                    signal: 'click',
                  } satisfies s.FeedbackRequest,
                }).catch(() => {
                  /* feedback is best-effort */
                });
                navigate({ to: '/brief/$id', params: { id: suggestion.entity_id } });
              },
              onDismiss: (suggestion) => {
                void apiFetch('/v1/search/feedback', {
                  method: 'POST',
                  body: {
                    query_id: crypto.randomUUID(),
                    opportunity_id: suggestion.entity_id,
                    signal: 'hide',
                  } satisfies s.FeedbackRequest,
                }).catch(() => {
                  /* feedback is best-effort */
                });
              },
              onMuteCategory: () => {
                /* category mute is a session-only UI hint for now */
              },
              initialMode: 'pinned',
            }
      }
      facetCounts={facetCounts}
      nlEditing={nlEditing}
      nlValue={nlValue}
      onNlEditChange={setNlValue}
      onNlSubmit={async (text) => {
        // Phase C: route through /v1/ai/nl-rewrite (services/inference,
        // template `nl_rewrite.v1`). The Haiku-class call returns a shallow
        // Predicate[] + leftover q. We compose the chips into FilterState
        // (text fields default to `like` per FieldSpec.defaultOp) and
        // route the leftover to the FTS `q` parameter.
        //
        // Fallback: if the route returns an error (no Anthropic/OpenAI key,
        // budget exhausted, parse failure), gracefully fall back to the
        // FTS-only path — never silently leave the user with no result.
        try {
          const out = await apiFetch<{
            predicates: { field: string; op: string; value: unknown }[];
            q: string | null;
          }>('/v1/ai/nl-rewrite', {
            method: 'POST',
            body: { text },
          });
          // Compose FilterState from predicates. We deliberately handle only
          // the shapes the rail can render; advanced AST nodes (or/not) flow
          // through the QueryBuilder when that ships in Phase C.
          const next: f.FilterState = { ...state };
          for (const p of out.predicates) {
            switch (p.op) {
              case 'in':
              case 'nin':
                if (Array.isArray(p.value)) next[p.field] = p.value as string[];
                break;
              case 'between':
                if (p.value && typeof p.value === 'object') {
                  next[p.field] = p.value as f.FilterValue;
                }
                break;
              case 'like':
              case 'eq':
              case 'ne':
              case 'gt':
              case 'gte':
              case 'lt':
              case 'lte':
                if (typeof p.value === 'string' || typeof p.value === 'boolean') {
                  next[p.field] = p.value;
                }
                break;
              // exists/missing don't carry a value; ignore for the flat
              // FilterState (they only make sense in the AST).
            }
          }
          setState(next);
          // Phase D: capture leftover for explicit user disposition. The
          // IntentBar renders it as an "ignored" chip with promote/drop.
          setNlLeftover(out.q && out.q.trim().length > 0 ? out.q : null);
        } catch {
          // Inference unavailable → graceful FTS fallback. We promote the
          // raw text directly because the user typed it intending to search.
          setQ(text);
          setNlLeftover(null);
        }
        setNlEditing(false);
      }}
      onActivateNl={() => setNlEditing(true)}
      onCancelNl={() => {
        setNlEditing(false);
        setNlValue(search.q ?? '');
      }}
      nlLeftover={nlLeftover}
      onPromoteNlLeftover={(text) => {
        setQ(text);
        setNlLeftover(null);
      }}
      onDropNlLeftover={() => setNlLeftover(null)}
      emptyState={
        <EmptyState
          glyph="OPP"
          reason={opps.error ? 'LOAD FAILED' : 'NO MATCHES'}
          detail={
            opps.error
              ? `the search backend returned an error · id ${opps.error instanceof ApiError && opps.error.requestId ? opps.error.requestId : 'unknown'}`
              : 'try removing a filter, broadening a date range, or pressing / to type a natural-language query.'
          }
          actions={
            <button type="button" className="btn xs gh" onClick={() => setState({})}>
              CLEAR ALL FILTERS
            </button>
          }
        />
      }
      meta={{
        backend: opps.data?.meta.backend ?? 'postgres-fts',
        latencyMs: opps.data?.meta.latencyMs,
        fromCache: opps.data?.meta.fromCache ?? false,
        sortLabel: `${sort.field}:${sort.dir}`,
        rightSlot:
          pin.count > 0 ? (
            <span
              className="mono"
              style={{
                fontSize: 10,
                color: 'var(--amber)',
                letterSpacing: 0.4,
                textTransform: 'uppercase',
              }}
              title={`${pin.count} pinned ${pin.count === 1 ? 'opportunity' : 'opportunities'}`}
            >
              {`★ ${pin.count} PINNED`}
            </span>
          ) : null,
      }}
    />
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 *  Cells
 * ────────────────────────────────────────────────────────────────────────── */

function StatusPill({ status }: { status: a.OpportunityRow['status'] }) {
  const tone =
    status === 'active'
      ? 'primary'
      : status === 'awarded'
        ? 'green'
        : status === 'expired'
          ? 'gray'
          : status === 'cancelled'
            ? 'red'
            : 'gray';
  return <Pill tone={tone}>{statusShort(status)}</Pill>;
}

function statusShort(s: a.OpportunityRow['status']): string {
  switch (s) {
    case 'active':
      return 'act';
    case 'expired':
      return 'exp';
    case 'cancelled':
      return 'can';
    case 'awarded':
      return 'awd';
    case 'draft':
      return 'drf';
    default:
      return 'unk';
  }
}
